import { useEffect, useState } from "react";
import { ArrowLeft, Check, Loader2, Sparkles } from "lucide-react";
import {
  Plans,
  Subscriptions,
  type CatalogResponse,
  type CatalogTier,
} from "../../../lib/apiTyped";
import { useApiCall } from "../../../lib/useToastError";
import { buildContactMailto } from "../../../lib/contact";
import { tierDisplayName } from "../../../lib/format";
import { Modal } from "../../shared/Modal";
import { Card } from "../../shared/Card";
import { Spinner } from "../../shared/Spinner";

type Props = {
  orgId: string;
  orgName: string;
  email: string;
  onContinue: () => void;
  onBack: () => void;
};

type PendingAction = "trial" | "tier-2" | "enterprise" | null;

export function StepPlans({
  orgId,
  orgName,
  email,
  onContinue,
  onBack,
}: Props) {
  const { run } = useApiCall();
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [confirmTrial, setConfirmTrial] = useState(false);
  const [pending, setPending] = useState<PendingAction>(null);

  useEffect(() => {
    let cancelled = false;
    Subscriptions.catalog()
      .then((res) => {
        if (!cancelled) setCatalog(res);
      })
      .catch((err) => {
        if (!cancelled) {
          setCatalogError(
            err instanceof Error ? err.message : "No pudimos cargar los planes.",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const proTier =
    catalog?.tiers.find((t) => t.slug === "tier-2") ?? null;
  const trialTier =
    catalog?.tiers.find((t) => t.slug === catalog?.trial.slug) ?? null;
  const trialAvailable = catalog?.trial.available ?? false;
  const trialDurationDays = catalog?.trial.durationDays ?? 14;

  async function activateTrial() {
    if (pending) return;
    setPending("trial");
    const ok = await run(() => Plans.activateTrial(orgId));
    setPending(null);
    if (ok) {
      setConfirmTrial(false);
      onContinue();
    }
  }

  async function requestPaidPlan(tier: "tier-2" | "enterprise") {
    if (pending) return;
    setPending(tier);
    const ok = await run(() => Plans.requestPlan(orgId, tier));
    setPending(null);
    if (ok) {
      const label = tierDisplayName(tier);
      window.location.href = buildContactMailto(
        `Activar plan ${label}`,
        { orgId, orgName, email },
        `Me gustaría activar el plan ${label} para mi organización.`,
      );
      onContinue();
    }
  }

  return (
    <section className="space-y-8">
      <div className="space-y-3">
        <p className="text-sm font-semibold uppercase tracking-wider text-ink-3">
          Paso 3 · Planes
        </p>
        <h1 className="text-4xl sm:text-6xl font-black tracking-tight text-ink leading-[1.05]">
          Elige tu plan
        </h1>
        <p className="text-base sm:text-lg text-ink-2 max-w-2xl">
          Empieza con la prueba gratuita o solicita un plan a medida.
        </p>
      </div>

      {catalogError && (
        <Card className="p-6">
          <p className="text-sm text-error">{catalogError}</p>
        </Card>
      )}

      {!catalog && !catalogError && (
        <div className="flex items-center justify-center py-10">
          <Spinner />
        </div>
      )}

      {catalog && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <TrialCard
            trial={trialTier}
            durationDays={trialDurationDays}
            available={trialAvailable}
            pending={pending === "trial"}
            disabled={pending !== null && pending !== "trial"}
            onActivate={() => setConfirmTrial(true)}
          />
          <ProCard
            tier={proTier}
            pending={pending === "tier-2"}
            disabled={pending !== null && pending !== "tier-2"}
            onRequest={() => requestPaidPlan("tier-2")}
          />
          <EnterpriseCard
            pending={pending === "enterprise"}
            disabled={pending !== null && pending !== "enterprise"}
            onRequest={() => requestPaidPlan("enterprise")}
          />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4 pt-2">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-sm text-ink-2 hover:text-ink transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Atrás
        </button>
      </div>

      {confirmTrial && trialTier && (
        <ConfirmTrialModal
          durationDays={trialDurationDays}
          credits={trialTier.monthlyCreditsLimit}
          maxSeats={trialTier.maxSeats}
          submitting={pending === "trial"}
          onConfirm={activateTrial}
          onClose={() => {
            if (pending === "trial") return;
            setConfirmTrial(false);
          }}
        />
      )}
    </section>
  );
}

function TrialCard({
  trial,
  durationDays,
  available,
  pending,
  disabled,
  onActivate,
}: {
  trial: CatalogTier | null;
  durationDays: number;
  available: boolean;
  pending: boolean;
  disabled: boolean;
  onActivate: () => void;
}) {
  const credits = trial?.monthlyCreditsLimit ?? 0;
  const seats = trial?.maxSeats ?? 0;
  return (
    <Card
      className={`p-6 flex flex-col gap-4 ${
        !available ? "opacity-60" : ""
      }`}
    >
      <div>
        <div className="flex items-center justify-between">
          <div className="text-lg font-black text-ink">Prueba gratuita</div>
          <span className="text-xs font-semibold uppercase tracking-wider text-ink bg-bg px-2 py-0.5 rounded-full">
            {durationDays} días
          </span>
        </div>
        <div className="mt-1 text-2xl font-black text-ink">
          Gratis
          <span className="text-sm font-normal text-ink-2"> · sin tarjeta</span>
        </div>
      </div>

      <ul className="space-y-2 text-sm text-ink-2 flex-1">
        <Feature label={`${fmtCredits(credits)} créditos`} suffix=" (uso único)" />
        <Feature label={`Hasta ${seats} usuarios`} />
        <Feature label={`${durationDays} días de uso`} suffix=" desde la activación" />
      </ul>

      {available ? (
        <button
          type="button"
          onClick={onActivate}
          disabled={disabled || pending || !trial}
          className="inline-flex items-center justify-center gap-2 bg-ink text-white text-sm font-semibold px-4 py-2 rounded-full transition-opacity hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {pending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Empezar prueba
        </button>
      ) : (
        <div className="text-sm text-ink-3 italic">
          Ya has usado tu prueba gratuita.
        </div>
      )}
    </Card>
  );
}

function ProCard({
  tier,
  pending,
  disabled,
  onRequest,
}: {
  tier: CatalogTier | null;
  pending: boolean;
  disabled: boolean;
  onRequest: () => void;
}) {
  const credits = tier?.monthlyCreditsLimit ?? 0;
  const seats = tier?.maxSeats ?? 0;
  const price = tier?.priceEurPerMonth ?? 0;
  return (
    <Card className="p-6 flex flex-col gap-4 ring-2 ring-ink/10">
      <div>
        <div className="flex items-center justify-between">
          <div className="text-lg font-black text-ink">Pro</div>
        </div>
        <div className="mt-1 text-2xl font-black text-ink">
          {fmtEur(price)}
          <span className="text-sm font-normal text-ink-2"> / mes</span>
        </div>
      </div>

      <ul className="space-y-2 text-sm text-ink-2 flex-1">
        <Feature label={`${fmtCredits(credits)} créditos`} suffix="/mes" />
        <Feature label={`Hasta ${seats} usuarios`} />
        <Feature label="Soporte por email" />
      </ul>

      <button
        type="button"
        onClick={onRequest}
        disabled={disabled || pending || !tier}
        className="inline-flex items-center justify-center gap-2 bg-ink text-white text-sm font-semibold px-4 py-2 rounded-full transition-opacity hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {pending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        Contactar para activar
      </button>
    </Card>
  );
}

function EnterpriseCard({
  pending,
  disabled,
  onRequest,
}: {
  pending: boolean;
  disabled: boolean;
  onRequest: () => void;
}) {
  return (
    <Card className="p-6 flex flex-col gap-4 bg-gradient-to-br from-ink/5 to-transparent">
      <div>
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-ink" />
          <div className="text-lg font-black text-ink">Enterprise</div>
        </div>
        <div className="mt-1 text-2xl font-black text-ink">A medida</div>
        <div className="text-sm text-ink-2">según tus necesidades</div>
      </div>

      <ul className="space-y-2 text-sm text-ink-2 flex-1">
        <Feature label="Volumen y rate limits personalizados" />
        <Feature label="Soporte prioritario y SLA" />
        <Feature label="Onboarding dedicado" />
      </ul>

      <button
        type="button"
        onClick={onRequest}
        disabled={disabled || pending}
        className="inline-flex items-center justify-center gap-2 bg-ink text-white text-sm font-semibold px-4 py-2 rounded-full transition-opacity hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {pending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        Hablar con nosotros
      </button>
    </Card>
  );
}

function Feature({ label, suffix }: { label: string; suffix?: string }) {
  return (
    <li className="flex items-start gap-2">
      <Check className="w-4 h-4 mt-0.5 text-ink shrink-0" />
      <span>
        {label}
        {suffix && <span className="text-ink-3">{suffix}</span>}
      </span>
    </li>
  );
}

function ConfirmTrialModal({
  durationDays,
  credits,
  maxSeats,
  submitting,
  onConfirm,
  onClose,
}: {
  durationDays: number;
  credits: number;
  maxSeats: number;
  submitting: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Modal onClose={onClose} maxWidth="26rem">
      <div className="space-y-5">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-ink-3 mb-2">
            Prueba gratuita
          </div>
          <h2 className="text-2xl font-black text-ink leading-tight">
            ¿Activar tu prueba?
          </h2>
        </div>
        <ul className="space-y-2 text-sm text-ink-2">
          <Feature label={`${fmtCredits(credits)} créditos`} suffix=" (uso único, no se renuevan)" />
          <Feature label={`Expira en ${durationDays} días`} />
          <Feature label={`Hasta ${maxSeats} usuarios en tu equipo`} />
          <Feature label="Una sola prueba por persona" />
        </ul>
        <div className="flex items-center gap-3 pt-2">
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            className="inline-flex items-center justify-center gap-2 bg-ink text-white text-sm font-semibold px-4 py-2 rounded-full transition-opacity hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Activar prueba
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-sm text-ink-2 hover:text-ink transition-colors disabled:opacity-40"
          >
            Cancelar
          </button>
        </div>
      </div>
    </Modal>
  );
}

function fmtCredits(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return String(n);
}

function fmtEur(n: number): string {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}
